import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Fade, Grid2, Paper } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { RootState } from "../../app/rootReducer";
import { useSelector } from "../../app/helpers";
import RestrictionPicker from "../../components/Pickers/restriction-picker.component";
import ScheduleCalendar from "../../components/Schedule/ScheduleCalendar/schedule-calendar.component";
import { getInitialVisibleMonthCalendarDateRange } from "../../components/Schedule/functions/schedule.functions";
import { InfoIconWithTooltip } from "../../components/Title/InfoIcon";
import { BookingScheduleInterface, RestrictionZone } from "../Booking-Form/typings/booking.types";
import { TeamMember } from "../Booking-Form/typings/team-member";
import { PricingModel } from "../Login/typings/login.types";
import { updateSelectedAppointment } from "./schedule.functions";
import { useRemoteInactivateBookings } from "../../hooks/Remote/Booking/useRemoteInactivateBookings";
import { useRemoteFetchPlaceSchedule } from "../../hooks/Remote/Schedule/useRemoteFetchPlaceSchedule";
import { useRemoteFetchNoPlaceZoneBookingSchedule } from "../../hooks/Remote/Schedule/useRemoteFetchNoPlaceZoneBookingSchedule";
import { useRemoteFetchVehicleSchedule } from "../../hooks/Remote/Schedule/useRemoteFetchVehicleSchedule";
import { useRemoteFetchBookableBookingObjects } from "../../hooks/Remote/Schedule/useRemoteFetchBookableBookingObjects";
import { useRemoteResourceCalendarByBookingObjectMutation } from "../../hooks/Remote/Schedule/useRemoteResourceCalendarByBookingObjectMutation";
import { useRemoteFetchVisitOnlySchedule } from "../../hooks/Remote/Schedule/useRemoteFetchVisitOnlySchedule";
import { useRemoteFetchScheduleColleagues } from "../../hooks/Remote/Connections/useRemoteFetchScheduleColleagues";
import { useRemoteUpdateStoredScheduleColleagues } from "../../hooks/Remote/Connections/useRemoteUpdateStoredScheduleColleagues";
import { useRemoteFetchStoredScheduleColleagues } from "../../hooks/Remote/Connections/useRemoteFetchStoredScheduleColleagues";
import { BookingType } from "../Booking-Form/typings/booking-inputs";
import {
  placeScheduleWithoutVisitOnly,
  updateScheduleData,
  updateScheduleDataStatus
} from "./functions/handleScheduleData.functions";
import { ResourceInactivityResponseDto } from "./typings/resource-calendar.types";
import { generatePrestoredColleagues } from "../../components/Schedule/ScheduleCalendar/functions/genPrestoredColleagues.function";
import { useRemoteFetchFirstAidUsers } from "../../hooks/Remote/OfficeSafety/useRemoteFetchFirstAidUsers";
import { calculateTagLimit } from "../../components/BookingForm/BookingFilter/functions/calculateTagLimit.function";
import { ConnectedBookingData } from "../Booking-Form/typings/connected-bookingData";
import { FirstAidOptions, SelectFirstAidOptions } from "./partial/ScheduleFirstAidSelect.partial";
import { ScheduleMultiDeleteButton } from "./partial/ScheduleMultiDeleteButton.partial";
import { ScheduleMode } from "./partial/ScheduleModeToggle.partial";
import { namePerType } from "../../components/FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import type { ScheduleResourceAutocompleteOption } from "./partial/ScheduleResourceAutocomplete.partial";
import type { ScheduleResourceBookCalendarPrefetch } from "./partial/ScheduleResourceBookDialog.partial";
import type { RefetchScheduleOptions } from "./typings/refetch-schedule.types";
import { handleFirstAidScheduleOptions } from "./functions/handleFirstAidScheduleOptions.function";
import { handleFirstAidFilterOptions } from "./functions/handleFirstAidFilterOptions.function";
import { syncResourceModeSchedulesFromBookableCatalog } from "./functions/syncResourceModeSchedulesFromBookableCatalog";
import { useFeatures } from "../../components/FacilityManager/Hooks/useFeatures";
import { selectVisitorManagementEnabled } from "../Login/selectors/selectVisitorManagementEnabled";
import { ScheduleViewMode } from "./partial/ScheduleViewToggle.partial";
import { ScheduleToolbar, scheduleToolbarPaperSx } from "./partial/ScheduleToolbar.partial";
import { ScheduleSelectionDialog } from "../../components/Schedule/ScheduleCalendar/ScheduleSelectionDialog/ScheduleSelectionDialog.component";
import { ScheduleTableView } from "./partial/ScheduleTableView.partial";
import type { ScheduleExcelExportFetchConfig } from "../../components/Schedule/ScheduleCalendar/functions/schedule-excel-export-fetch.functions";
import { useRemoteFetchColleaguesQuery } from "../Connections/hooks/useRemoteFetchColleaguesQuery";
import { generateScheduleType } from "../../components/Schedule/ScheduleCalendar/ScheduleSelectionDialog/hooks/generateScheduleType.hook.function";
import BookingSvgDialog from "../../components/Svg/svg-dialog.component";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const scheduleEmbedShellSx: SxProps<Theme> = {
  width: "100%",
  flexShrink: 0
};

const scheduleEmbedRootSx: SxProps<Theme> = {
  width: "100%",
  flexShrink: 0
};

type ScheduleDataFetchStatus = "error" | "loading" | "success" | "idle";

export type ScheduleEmbedConfig = {
  hideToolbar: boolean;
  scheduleMode: ScheduleMode;
  onScheduleModeChange: (mode: ScheduleMode) => void;
  scheduleViewMode: ScheduleViewMode;
  onScheduleViewModeChange: (mode: ScheduleViewMode) => void;
  selectedColleagues: TeamMember[];
  onSelectedColleaguesChange: (colleagues: TeamMember[]) => void;
  colleagues: TeamMember[];
  preStoredColleagues: string[];
  scheduleColleaguesIsFetching: boolean;
  storedScheduleColleaguesIsFetching: boolean;
  onStoreSelectedColleagues: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
  tagLimit: number;
  selectedResource: ScheduleResourceAutocompleteOption | null;
  onSelectedResourceChange: (resource: ScheduleResourceAutocompleteOption | null) => void;
  resourceBookingType: BookingType | null;
  onResourceBookingTypeChange: (type: BookingType | null) => void;
  resourceAvailableTypes: BookingType[];
  resourceAutocompleteLoading: boolean;
  bookableBookingObjectsError: boolean;
  resourceAutocompleteOptions: ScheduleResourceAutocompleteOption[];
  onResourceFetchEnable: () => void;
  viewToggleSlot?: React.ReactNode;
  contentContainerSx?: SxProps<Theme>;
};

type P = {
  isQuickAutomatedPlaceBooking: boolean;
  setIsQuickAutomatedPlaceBooking: (q: boolean) => void;
  embed?: ScheduleEmbedConfig;
};

/**
 * @description The schedule page renders the whole user dropdown and year selection. It controls the workplace and zone schedule view.
 */
const Schedule: React.FC<P> = ({
  isQuickAutomatedPlaceBooking,
  setIsQuickAutomatedPlaceBooking,
  embed
}) => {
  const { t, i18n } = useTranslation();
  const { userInformation } = useSelector((state: RootState) => state.login);
  const {
    sub,
    firstName,
    surname,
    pricingModels,
    placeTypes,
    zoneTypes,
    company: {
      meta: { workplaceBookingEnabled }
    }
  } = userInformation;
  const { useFirstAidUserFilter } = useFeatures();
  const visitorManagementEnabled = useSelector(selectVisitorManagementEnabled);

  const internalContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = embed?.containerRef ?? internalContainerRef;

  const [tagLimit, setTagLimit] = useState(2);
  const [internalScheduleMode, setInternalScheduleMode] = useState<ScheduleMode>(
    ScheduleMode.Colleague
  );
  const [internalScheduleViewMode, setInternalScheduleViewMode] = useState<ScheduleViewMode>(
    ScheduleViewMode.Calendar
  );

  const scheduleMode = embed?.scheduleMode ?? internalScheduleMode;
  const setScheduleMode = embed?.onScheduleModeChange ?? setInternalScheduleMode;
  const scheduleViewMode = embed?.scheduleViewMode ?? internalScheduleViewMode;
  const setScheduleViewMode = embed?.onScheduleViewModeChange ?? setInternalScheduleViewMode;

  const [schedules, setSchedules] = useState<BookingScheduleInterface[]>([]);
  const [scheduleDataStatus, setScheduleDataStatus] = useState<ScheduleDataFetchStatus>("idle");
  const [calendarDate, setCalendarDate] = useState<{
    firstDay: string | undefined;
    lastDay: string | undefined;
  }>({ firstDay: undefined, lastDay: undefined });

  const [deletedBookings, setDeletedBookings] = useState<boolean>(false);

  const [selectedAppointments, setSelectedAppointments] = useState<number[]>([]);
  const [internalSelectedColleagues, setInternalSelectedColleagues] = useState<TeamMember[]>([
    { firstName: firstName, surname: surname, userId: sub }
  ]);
  const [internalColleagues, setInternalColleagues] = useState<TeamMember[]>([]);
  const [preStoredColleagues, setPreStoredColleagues] = useState<string[]>([]);

  const selectedColleagues = embed?.selectedColleagues ?? internalSelectedColleagues;
  const setSelectedColleagues = embed?.onSelectedColleaguesChange ?? setInternalSelectedColleagues;
  const colleagues = embed?.colleagues ?? internalColleagues;
  const setColleagues = embed ? () => undefined : setInternalColleagues;

  const [isFirstAidSelected, setIsFirstAidSelected] = useState(false);
  const [firstAidOptions, setFirstAidOptions] = useState<FirstAidOptions[]>([]);
  const [selectFirstAidOptions, setSelectFirstAidOptions] = useState<SelectFirstAidOptions>({
    options: [],
    reason: null
  });
  const [allFirstAidSchedules, setAllFirstAidSchedules] = useState<BookingScheduleInterface[]>([]);

  const [internalSelectedResource, setInternalSelectedResource] =
    useState<ScheduleResourceAutocompleteOption | null>(null);
  const [internalResourceBookingType, setInternalResourceBookingType] =
    useState<BookingType | null>(null);
  const selectedResource = embed?.selectedResource ?? internalSelectedResource;
  const setSelectedResourceState = embed?.onSelectedResourceChange ?? setInternalSelectedResource;
  const setSelectedResource = useCallback(
    (value: React.SetStateAction<ScheduleResourceAutocompleteOption | null>) => {
      if (typeof value === "function") {
        setSelectedResourceState(value(selectedResource));
        return;
      }
      setSelectedResourceState(value);
    },
    [selectedResource, setSelectedResourceState]
  );
  const resourceBookingType = embed?.resourceBookingType ?? internalResourceBookingType;
  const setResourceBookingType =
    embed?.onResourceBookingTypeChange ?? setInternalResourceBookingType;
  /** Dedupes `by-booking-object` when the effect re-runs after syncing `searchLabel` onto `selectedResource`. */
  const resourceScheduleByObjectKeyRef = useRef<string>("");
  const [resourceSchedules, setResourceSchedules] = useState<BookingScheduleInterface[]>([]);
  const [resourceInactivity, setResourceInactivity] =
    useState<ResourceInactivityResponseDto | null>(null);
  const [resourceScheduleStatus, setResourceScheduleStatus] =
    useState<ScheduleDataFetchStatus>("idle");

  const [selected, setSelected] = useState<RestrictionZone>({
    company: { id: 0, name: "" },
    object: { id: 0, zoneBookingObject: "", type: "", name: "" }
  } as RestrictionZone);

  const isColleagueMode = scheduleMode === ScheduleMode.Colleague;
  const isResourceMode = scheduleMode === ScheduleMode.Resource;
  const isTableView = scheduleViewMode === ScheduleViewMode.Table;

  const [showBookingDetail, setShowBookingDetail] = useState<{
    schedule: BookingScheduleInterface | null;
    open: boolean;
  }>({ schedule: null, open: false });

  const [selectedSchedule, setSelectedSchedule] = useState<BookingScheduleInterface | undefined>(
    undefined
  );
  const [isShowFloorDialogOpen, setIsShowFloorDialogOpen] = useState(false);

  const handleShowFloor = useCallback((schedule: BookingScheduleInterface) => {
    if (schedule.id == null) {
      return;
    }
    setSelectedSchedule(schedule);
    setIsShowFloorDialogOpen(true);
  }, []);

  const handleShowFloorCloseFn = useCallback(() => {
    setSelectedSchedule(undefined);
    setIsShowFloorDialogOpen(false);
  }, []);

  const userIds = selectedColleagues
    ? selectedColleagues.map((user: { userId: string }) => user.userId)
    : [sub];

  const hasVehicleLicenseForUser = placeTypes.some(type => type.name === BookingType.VEHICLE);
  const hasConferenceLicenseForUser = zoneTypes.some(
    type => type.name === BookingType.CONFERENCEZONE
  );
  const resourceAvailableTypes = useMemo(
    () =>
      embed?.resourceAvailableTypes ?? [
        ...(workplaceBookingEnabled && placeTypes.some(type => type.name === BookingType.WORKPLACE)
          ? [BookingType.WORKPLACE]
          : []),
        ...(zoneTypes.some(type => type.name === BookingType.CONFERENCEZONE)
          ? [BookingType.CONFERENCEZONE]
          : []),
        ...(placeTypes.some(type => type.name === BookingType.VEHICLE) ? [BookingType.VEHICLE] : [])
      ],
    [embed?.resourceAvailableTypes, placeTypes, zoneTypes, workplaceBookingEnabled]
  );

  const {
    data: placeScheduleData,
    isFetching: placeScheduleDataIsFetching,
    refetch: refetchPlaceSchedule,
    status: placeScheduleDataStatus
  } = useRemoteFetchPlaceSchedule({
    bookingScheduleDto: {
      userIds: userIds,
      startDate: calendarDate.firstDay,
      endDate: calendarDate.lastDay
    },
    workplaceBookingEnabled: workplaceBookingEnabled && isColleagueMode
  });

  const {
    data: visitOnlyScheduleData,
    isFetching: visitOnlyScheduleDataIsFetching,
    refetch: refetchVisitOnlySchedule,
    status: visitOnlyScheduleDataStatus
  } = useRemoteFetchVisitOnlySchedule(
    {
      userIds: userIds,
      startDate: calendarDate.firstDay,
      endDate: calendarDate.lastDay
    },
    visitorManagementEnabled
  );
  const {
    data: noPlaceZoneBookingScheduleData,
    isFetching: noPlaceZoneBookingScheduleDataIsFetching,
    refetch: refetchNoPlaceZoneBookingSchedule,
    status: noPlaceZoneBookingScheduleDataStatus
  } = useRemoteFetchNoPlaceZoneBookingSchedule({
    bookingScheduleDto: {
      userIds: userIds,
      startDate: calendarDate.firstDay,
      endDate: calendarDate.lastDay
    },
    hasNoPlaceZoneLicense: hasConferenceLicenseForUser && isColleagueMode
  });

  const {
    data: vehicleScheduleData,
    isFetching: vehicleScheduleDataIsFetching,
    refetch: refetchVehicleSchedule,
    status: vehicleScheduleDataStatus
  } = useRemoteFetchVehicleSchedule({
    bookingScheduleDto: {
      userIds: userIds,
      startDate: calendarDate.firstDay,
      endDate: calendarDate.lastDay
    },
    hasVehicleLicense: hasVehicleLicenseForUser && isColleagueMode
  });

  const remoteBookableBookingObjects = useRemoteFetchBookableBookingObjects({
    enabled: isResourceMode,
    endDate: calendarDate.lastDay,
    startDate: calendarDate.firstDay,
    bookingType: resourceBookingType ?? BookingType.WORKPLACE,
    includeInactive: true
  });

  const bookableBookingObjects = resourceBookingType
    ? remoteBookableBookingObjects.data?.filter(
        option => option.bookingType === resourceBookingType
      )
    : remoteBookableBookingObjects.data;

  const bookableBookingObjectsFetching = remoteBookableBookingObjects.isFetching;

  const bookableBookingObjectsError = remoteBookableBookingObjects.isError;

  const { mutateAsync: fetchResourceCalendarByBookingObject } =
    useRemoteResourceCalendarByBookingObjectMutation();

  const refetchAllSchedule = useCallback(() => {
    if (!isColleagueMode) return;

    if (workplaceBookingEnabled) refetchPlaceSchedule();
    if (hasConferenceLicenseForUser) refetchNoPlaceZoneBookingSchedule();
    if (hasVehicleLicenseForUser) refetchVehicleSchedule();
    if (visitorManagementEnabled) refetchVisitOnlySchedule();
  }, [
    hasConferenceLicenseForUser,
    hasVehicleLicenseForUser,
    isColleagueMode,
    refetchNoPlaceZoneBookingSchedule,
    refetchPlaceSchedule,
    refetchVehicleSchedule,
    refetchVisitOnlySchedule,
    visitorManagementEnabled,
    workplaceBookingEnabled
  ]);

  const refetchSelectedResourceSchedule = useCallback(
    (options?: RefetchScheduleOptions) => {
      if (!isResourceMode || !selectedResource || !calendarDate.firstDay || !calendarDate.lastDay) {
        return;
      }

      const requestKey = `${selectedResource.bookingInventoryId}\0${selectedResource.bookingType}\0${calendarDate.firstDay}\0${calendarDate.lastDay}`;
      if (!options?.force && resourceScheduleByObjectKeyRef.current === requestKey) {
        return;
      }
      resourceScheduleByObjectKeyRef.current = requestKey;

      setResourceScheduleStatus("loading");
      fetchResourceCalendarByBookingObject({
        bookingInventoryId: selectedResource.bookingInventoryId,
        bookingType: selectedResource.bookingType,
        startDate: calendarDate.firstDay,
        endDate: calendarDate.lastDay
      })
        .then(({ schedules, inactivity }) => {
          setResourceSchedules(schedules);
          setResourceInactivity(inactivity);
          setResourceScheduleStatus("success");
        })
        .catch(() => {
          setResourceSchedules([]);
          setResourceInactivity(null);
          setResourceScheduleStatus("error");
        });
    },
    [
      calendarDate.firstDay,
      calendarDate.lastDay,
      fetchResourceCalendarByBookingObject,
      isResourceMode,
      selectedResource
    ]
  );

  const refetchSchedulesForTable = useCallback(() => {
    if (isColleagueMode) {
      refetchAllSchedule();
    } else {
      refetchSelectedResourceSchedule();
    }
  }, [isColleagueMode, refetchAllSchedule, refetchSelectedResourceSchedule]);

  const { data: scheduleColleaguesData, isFetching: scheduleColleaguesIsFetching } =
    useRemoteFetchScheduleColleagues();

  const { data: remoteColleagues } = useRemoteFetchColleaguesQuery();

  // office safety - first aid users
  const { mutateAsync: getFirstAidUsers } = useRemoteFetchFirstAidUsers();

  const {
    data: storedScheduleColleaguesData,
    isFetching: storedScheduleColleaguesIsFetching,
    refetch: refetchStoredScheduleColleagues
  } = useRemoteFetchStoredScheduleColleagues();

  const { mutateAsync: updateStoredScheduleColleagues } = useRemoteUpdateStoredScheduleColleagues();
  const handleStoredScheduleColleagues = () => {
    if (embed) {
      embed.onStoreSelectedColleagues();
      return;
    }

    updateStoredScheduleColleagues({ userIds: selectedColleagues.map(col => col.userId) }).then(
      () => refetchStoredScheduleColleagues()
    );
  };

  const { mutate: inactivateBookings, status: inactivateBookingsStatus } =
    useRemoteInactivateBookings();

  const updateAppointmentData = (appointment: number) =>
    updateSelectedAppointment(appointment, selectedAppointments, setSelectedAppointments);

  const handleToggleFirstAidSelected = useCallback(() => {
    setIsFirstAidSelected(prev => !prev);
  }, []);

  const noopSetFirstAidSelected = useCallback(() => {}, []);

  useEffect(() => {
    if (!scheduleColleaguesData) return;
    if (!embed) {
      setColleagues(scheduleColleaguesData);

      const preStored = generatePrestoredColleagues({
        user: { userBookedById: sub, firstName: firstName, surname: surname, userId: sub },
        sub,
        scheduleColleaguesData,
        storedScheduleColleaguesData: storedScheduleColleaguesData ?? []
      });

      setPreStoredColleagues(preStored.map(col => col.userId));
      setSelectedColleagues(preStored);
    }
  }, [
    embed,
    sub,
    scheduleColleaguesData,
    storedScheduleColleaguesData,
    firstName,
    surname,
    setSelectedColleagues
  ]);

  useEffect(() => {
    if (!isColleagueMode) {
      return;
    }

    updateScheduleData(
      placeScheduleData,
      visitorManagementEnabled ? visitOnlyScheduleData : undefined,
      noPlaceZoneBookingScheduleData,
      vehicleScheduleData,
      setSchedules
    );
    // if first aid toggle is on, look for location and floor options
    /* istanbul ignore next */
    if (isFirstAidSelected && useFirstAidUserFilter)
      // don't use first aid filter options for now
      handleFirstAidScheduleOptions({
        placeScheduleData: placeScheduleWithoutVisitOnly(placeScheduleData),
        noPlaceZoneBookingScheduleData, // excludes vehicleScheduleData and visitOnlyScheduleData
        setFirstAidOptions
      });

    const isScheduleLoading =
      (workplaceBookingEnabled && placeScheduleDataIsFetching) ||
      (visitorManagementEnabled && visitOnlyScheduleDataIsFetching) ||
      (hasVehicleLicenseForUser && vehicleScheduleDataIsFetching) ||
      (hasConferenceLicenseForUser && noPlaceZoneBookingScheduleDataIsFetching);

    updateScheduleDataStatus({
      enabled: {
        workplaceBookingEnabled,
        hasVehicleLicense: hasVehicleLicenseForUser,
        hasConferenceLicense: hasConferenceLicenseForUser,
        visitorManagementEnabled
      },
      status: {
        isScheduleLoading,
        placeScheduleDataStatus,
        visitOnlyScheduleDataStatus,
        noPlaceZoneBookingScheduleDataStatus,
        vehicleScheduleDataStatus,
        setScheduleDataStatus
      }
    });
  }, [
    isColleagueMode,
    hasVehicleLicenseForUser,
    hasConferenceLicenseForUser,
    placeScheduleData,
    visitOnlyScheduleData,
    noPlaceZoneBookingScheduleData,
    vehicleScheduleData,
    placeScheduleDataIsFetching,
    visitOnlyScheduleDataIsFetching,
    noPlaceZoneBookingScheduleDataIsFetching,
    vehicleScheduleDataIsFetching,
    placeScheduleDataStatus,
    noPlaceZoneBookingScheduleDataStatus,
    vehicleScheduleDataStatus,
    visitOnlyScheduleDataStatus,
    visitorManagementEnabled,
    setScheduleDataStatus
  ]);

  useEffect(() => {
    if (inactivateBookingsStatus === "error") {
      setSelectedAppointments([]);
    }
    if (inactivateBookingsStatus === "success") {
      setDeletedBookings(false);
      setSelectedAppointments([]);
    }
  }, [inactivateBookingsStatus]);

  useEffect(() => {
    if (isQuickAutomatedPlaceBooking) {
      setIsQuickAutomatedPlaceBooking(false);
      refetchAllSchedule();
    }
  }, [isQuickAutomatedPlaceBooking, refetchAllSchedule, setIsQuickAutomatedPlaceBooking]);

  useEffect(() => {
    if (!scheduleColleaguesData || !storedScheduleColleaguesData) return;
    if (!isColleagueMode) return;

    setSchedules([]);

    // if true, select firstAid user in the field and show only firstAid users' booking on the calendar
    if (isFirstAidSelected) {
      getFirstAidUsers()
        .then(data => {
          const firstAidUsers = data.map((col, index) => ({
            ...col,
            userId: col.id,
            firstName: `${index + 1}_firstNameForFirstAidUser`,
            surname: ""
          }));
          // update selected colleagues to GET their booking schedules
          setSelectedColleagues(firstAidUsers);
          // update colleagues field options.
          /* istanbul ignore next */
          if (!useFirstAidUserFilter) setColleagues(firstAidUsers);
        })
        .catch(() => setSelectedColleagues([]));
    }
    // if untoggle, show prestored colleagues booking back again
    else {
      const preStored = generatePrestoredColleagues({
        user: { userBookedById: sub, firstName: firstName, surname: surname, userId: sub },
        sub,
        scheduleColleaguesData,
        storedScheduleColleaguesData
      });
      setSelectedColleagues(preStored);

      // reset first aid state. don't use first aid filter options for now
      if (useFirstAidUserFilter) {
        setAllFirstAidSchedules([]);
        setFirstAidOptions([]);
        setSelectFirstAidOptions({ options: [], reason: null });
      }
    }
  }, [isFirstAidSelected, isColleagueMode]);

  useEffect(() => {
    /* istanbul ignore next */
    if (!isFirstAidSelected || !firstAidOptions.length) return;

    // handle when first aid filtering works
    handleFirstAidFilterOptions({
      schedules,
      selectFirstAidOptions,
      allFirstAidSchedules,
      setSchedules,
      setAllFirstAidSchedules
    });
  }, [firstAidOptions, selectFirstAidOptions]);

  useEffect(() => {
    if (!isColleagueMode) {
      setIsFirstAidSelected(false);
    }
  }, [isColleagueMode]);

  useEffect(() => {
    if (!isColleagueMode) return;
    // reset resource booking state
    setSelectedResource(null);
    setResourceBookingType(null);
    setResourceSchedules([]);
    setResourceInactivity(null);
    setResourceScheduleStatus("idle");
  }, [isColleagueMode]);

  useEffect(() => {
    if (!isResourceMode) return;
    if (resourceAvailableTypes.length === 0) {
      setResourceBookingType(null);
      return;
    }
    if (resourceBookingType && resourceAvailableTypes.includes(resourceBookingType)) {
      return;
    }
    setResourceBookingType(resourceAvailableTypes[0]);
  }, [isResourceMode, resourceAvailableTypes, resourceBookingType]);

  const resourceAutocompleteOptions: ScheduleResourceAutocompleteOption[] = useMemo(
    () =>
      (bookableBookingObjects ?? []).map(option => ({
        ...option,
        searchLabel: [
          t(namePerType(option.bookingType, t)),
          option.locationName,
          option.floorName,
          option.objectName
        ]
          .filter(Boolean)
          .join(" - ")
      })),
    [bookableBookingObjects, t]
  );

  const resourceAutocompleteOptionsRef = useRef(resourceAutocompleteOptions);
  resourceAutocompleteOptionsRef.current = resourceAutocompleteOptions;

  const selectedResourceRef = useRef(selectedResource);
  selectedResourceRef.current = selectedResource;

  const bookableCatalogSyncToken = useMemo(() => {
    if (!bookableBookingObjects?.length) {
      return "";
    }
    return bookableBookingObjects
      .map(
        o =>
          `${o.bookingInventoryId}\0${o.bookingType}\0${o.locationName ?? ""}\0${o.floorName ?? ""}\0${
            o.objectName ?? ""
          }`
      )
      .sort()
      .join("\n");
  }, [bookableBookingObjects]);

  const selectedResourceStableKey =
    selectedResource == null
      ? ""
      : `${selectedResource.bookingInventoryId}\0${selectedResource.bookingType}`;

  const resourceAutocompleteLoading =
    bookableBookingObjectsFetching ||
    (isResourceMode && selectedResource != null && resourceScheduleStatus === "loading");

  const resourceBookCalendarPrefetch = useMemo((): ScheduleResourceBookCalendarPrefetch | null => {
    if (
      !isResourceMode ||
      !selectedResource ||
      !calendarDate.firstDay ||
      !calendarDate.lastDay ||
      resourceScheduleStatus !== "success"
    ) {
      return null;
    }
    return {
      inactivity: resourceInactivity ?? undefined,
      schedules: resourceSchedules,
      rangeStartIso: calendarDate.firstDay,
      rangeEndIso: calendarDate.lastDay
    };
  }, [
    calendarDate.firstDay,
    calendarDate.lastDay,
    isResourceMode,
    resourceInactivity,
    resourceScheduleStatus,
    resourceSchedules,
    selectedResource
  ]);

  useEffect(() => {
    syncResourceModeSchedulesFromBookableCatalog({
      isResourceMode,
      calendarFirstDay: calendarDate.firstDay,
      calendarLastDay: calendarDate.lastDay,
      bookableBookingObjectsFetching,
      selectedResource: selectedResourceRef.current,
      resourceAutocompleteOptions: resourceAutocompleteOptionsRef.current,
      resourceScheduleByObjectKeyRef,
      setSelectedResource,
      setResourceSchedules,
      setResourceInactivity,
      setResourceScheduleStatus,
      fetchResourceCalendarByBookingObject
    });
  }, [
    bookableBookingObjectsFetching,
    bookableCatalogSyncToken,
    calendarDate.firstDay,
    calendarDate.lastDay,
    fetchResourceCalendarByBookingObject,
    i18n.language,
    isResourceMode,
    selectedResourceStableKey
  ]);

  // Observe Autocomplete container width changes
  useEffect(() => {
    if (selectedColleagues.length > 0) {
      const mappedColleagues: ConnectedBookingData[] = selectedColleagues.map(colleague => ({
        firstName: colleague.firstName ?? "",
        lastName: colleague.surname ?? "",
        userId: colleague.userId ?? "",
        email: colleague.email ?? "",
        bookingInventoryId: colleague.bookingInventoryId ?? 0
      }));

      const resizeObserver = new ResizeObserver(() => {
        //122 = width + margin (88) of autocomplete colleague icons and
        // additional 34 px to make sure the chips stay in 1 line
        calculateTagLimit(containerRef, 132, mappedColleagues, setTagLimit);
      });

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      // Initial calculation
      calculateTagLimit(containerRef, 132, mappedColleagues, setTagLimit);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [selectedColleagues]);

  const visibleSchedules = isColleagueMode ? schedules : resourceSchedules;

  const excelExportInitialRange = useMemo(
    () =>
      calendarDate.firstDay && calendarDate.lastDay
        ? { startDate: calendarDate.firstDay, endDate: calendarDate.lastDay }
        : null,
    [calendarDate.firstDay, calendarDate.lastDay]
  );

  const excelExportFetchConfig = useMemo((): ScheduleExcelExportFetchConfig => {
    if (isColleagueMode) {
      return {
        scheduleMode: ScheduleMode.Colleague,
        userIds,
        workplaceBookingEnabled,
        hasConferenceLicense: hasConferenceLicenseForUser,
        hasVehicleLicense: hasVehicleLicenseForUser
      };
    }

    return {
      scheduleMode: ScheduleMode.Resource,
      resourceBookingType,
      resourceAvailableTypes
    };
  }, [
    isColleagueMode,
    userIds,
    workplaceBookingEnabled,
    hasConferenceLicenseForUser,
    hasVehicleLicenseForUser,
    resourceBookingType,
    resourceAvailableTypes
  ]);

  const canSelectScheduleForBulkDelete = useCallback(
    (schedule: BookingScheduleInterface) => {
      if (schedule.id == null) return false;
      const { isUserScheduleDeletable, isColleagueScheduleEditable } = generateScheduleType({
        schedule,
        userInformation,
        remoteColleagues
      });
      return isUserScheduleDeletable || isColleagueScheduleEditable;
    },
    [remoteColleagues, userInformation]
  );

  const wasTableViewRef = useRef(false);

  // Table view does not mount the calendar, so `updateCalendarDate` never runs—seed a month range
  // so schedule queries are enabled. Table then calls `onDateRangeRefetch` when the range is set.
  useEffect(() => {
    if (!isTableView) {
      return;
    }
    if (calendarDate.firstDay && calendarDate.lastDay) {
      return;
    }
    setCalendarDate(getInitialVisibleMonthCalendarDateRange());
  }, [isTableView, calendarDate.firstDay, calendarDate.lastDay]);

  // reset selected appointments when switching between table and calendar view
  useEffect(() => {
    if (!isColleagueMode) {
      setSelectedAppointments([]);
      wasTableViewRef.current = isTableView;
      return;
    }
    if (wasTableViewRef.current && !isTableView) {
      setSelectedAppointments([]);
    }
    wasTableViewRef.current = isTableView;
  }, [isColleagueMode, isTableView]);

  const renderScheduleContent = () => {
    if (isTableView) {
      const tableViewLoading = isColleagueMode
        ? scheduleDataStatus === "loading"
        : resourceScheduleStatus === "loading";

      return (
        <ScheduleTableView
          schedules={visibleSchedules}
          isLoading={tableViewLoading}
          scheduleDataStatus={isColleagueMode ? scheduleDataStatus : resourceScheduleStatus}
          refetchAllSchedule={
            isColleagueMode ? refetchAllSchedule : refetchSelectedResourceSchedule
          }
          users={isColleagueMode ? selectedColleagues : undefined}
          dateRangeStart={calendarDate.firstDay}
          dateRangeEnd={calendarDate.lastDay}
          onDateRangeRefetch={refetchSchedulesForTable}
          bulkSelectForDeleteEnabled={isColleagueMode}
          selectedAppointmentIds={selectedAppointments}
          onShowDetails={schedule => setShowBookingDetail({ schedule, open: true })}
          onSelectedAppointmentIdsChange={setSelectedAppointments}
          canSelectScheduleForBulkDelete={canSelectScheduleForBulkDelete}
          excelExportInitialRange={excelExportInitialRange}
          excelExportFetchConfig={excelExportFetchConfig}
        />
      );
    }

    if (isColleagueMode) {
      return (
        <ScheduleCalendar
          scheduleMode={scheduleMode}
          data-testid="colleague-schedule-calendar"
          monthView={true}
          deletingBookings={deletedBookings}
          setDeletingBookings={setDeletedBookings}
          setSelectedAppointment={updateAppointmentData}
          selectedAppointments={selectedAppointments}
          selectedColleagues={selectedColleagues}
          selectedRestriction={selected}
          users={selectedColleagues}
          schedules={schedules}
          scheduleDataStatus={scheduleDataStatus}
          setCalendarDate={setCalendarDate}
          refetchAllSchedule={refetchAllSchedule}
          isFirstAidSelected={isFirstAidSelected}
          setIsFirstAidSelected={handleToggleFirstAidSelected}
          excelExportInitialRange={excelExportInitialRange}
          excelExportFetchConfig={excelExportFetchConfig}
        />
      );
    }

    return (
      <ScheduleCalendar
        scheduleMode={scheduleMode}
        data-testid="resource-schedule-calendar"
        monthView={true}
        resourceBookCalendarPrefetch={resourceBookCalendarPrefetch}
        resourceInactivity={resourceInactivity}
        resourceBookingType={resourceBookingType}
        selectedRestriction={selected}
        selectedResource={selectedResource}
        schedules={resourceSchedules}
        scheduleDataStatus={resourceScheduleStatus}
        setCalendarDate={setCalendarDate}
        refetchAllSchedule={refetchSelectedResourceSchedule}
        isFirstAidSelected={false}
        setIsFirstAidSelected={noopSetFirstAidSelected}
        excelExportInitialRange={excelExportInitialRange}
        excelExportFetchConfig={excelExportFetchConfig}
      />
    );
  };

  const scheduleBody = (
    <>
      {!embed?.hideToolbar && (
        <Fade in={true}>
          <Paper elevation={0} sx={theme => scheduleToolbarPaperSx(theme)}>
            <ScheduleToolbar
              scheduleMode={scheduleMode}
              onScheduleModeChange={setScheduleMode}
              scheduleViewMode={scheduleViewMode}
              onScheduleViewModeChange={setScheduleViewMode}
              viewToggleSlot={embed?.viewToggleSlot}
              multiDeleteSlot={
                isColleagueMode && selectedAppointments.length > 0 ? (
                  <ScheduleMultiDeleteButton
                    schedules={visibleSchedules}
                    selectedAppointments={selectedAppointments}
                    setDeletedBookings={setDeletedBookings}
                    inactivateBookings={inactivateBookings}
                  />
                ) : undefined
              }
              isColleagueMode={isColleagueMode}
              isResourceMode={isResourceMode}
              containerRef={containerRef}
              tagLimit={embed?.tagLimit ?? tagLimit}
              colleagues={colleagues}
              selectedColleagues={selectedColleagues}
              scheduleColleaguesIsFetching={scheduleColleaguesIsFetching}
              storedScheduleColleaguesIsFetching={storedScheduleColleaguesIsFetching}
              preStoredColleagues={embed?.preStoredColleagues ?? preStoredColleagues}
              isFirstAidSelected={isFirstAidSelected}
              onSelectedColleaguesChange={setSelectedColleagues}
              onStoreSelectedColleagues={handleStoredScheduleColleagues}
              useFirstAidUserFilter={useFirstAidUserFilter}
              firstAidOptions={firstAidOptions}
              selectFirstAidOptions={selectFirstAidOptions}
              onSelectFirstAidOptionsChange={setSelectFirstAidOptions}
              resourceAvailableTypes={resourceAvailableTypes}
              resourceBookingType={resourceBookingType}
              onResourceBookingTypeChange={setResourceBookingType}
              onResourceFetchEnable={() => {
                setSelectedResource(null);
                setResourceSchedules([]);
                setResourceScheduleStatus("idle");
              }}
              resourceAutocompleteLoading={resourceAutocompleteLoading}
              bookableBookingObjectsError={bookableBookingObjectsError}
              resourceAutocompleteOptions={resourceAutocompleteOptions}
              selectedResource={selectedResource}
              onSelectedResourceChange={value => {
                setSelectedResource(value);
                setResourceSchedules([]);
              }}
            />
          </Paper>
        </Fade>
      )}

      <Fade
        mountOnEnter
        unmountOnExit
        in={
          isColleagueMode && selectedColleagues && pricingModels.includes(PricingModel.ENTERPRISE)
        }
      >
        <Grid2 container direction={"row"} alignItems={"flex-end"} justifyContent={"flex-start"}>
          <RestrictionPicker
            userId={selectedColleagues[0]?.userId || sub}
            mode={false}
            selection={selected}
            setSelection={setSelected}
          />
          <InfoIconWithTooltip
            tooltipText={t("Choose your company units that you have booked a zone for")}
          />
        </Grid2>
      </Fade>

      {/* render schedule content - calendar view or table view */}
      {embed?.contentContainerSx ? (
        <Box sx={scheduleEmbedShellSx} data-testid="schedule-embed-shell">
          <Box sx={embed.contentContainerSx} data-testid="schedule-embed-content">
            {renderScheduleContent()}
          </Box>
        </Box>
      ) : (
        renderScheduleContent()
      )}

      {/* schedule selection dialog, but only show if table view */}
      <ScheduleSelectionDialog
        scheduleMode={scheduleMode}
        isCalendarView={!isTableView}
        isEditable={false}
        isOpen={Boolean(showBookingDetail.schedule) && showBookingDetail.open}
        onClose={() => setShowBookingDetail({ schedule: null, open: false })}
        schedule={(showBookingDetail.schedule ?? {}) as BookingScheduleInterface}
        schedulePictures={[]}
        pictureIsLoading={false}
        allSchedules={visibleSchedules}
        calendarData={[]}
        isFirstAidSelected={false}
        onScheduleChange={handleShowFloor}
      />

      {selectedSchedule?.id != null && (
        <BookingSvgDialog
          isShowDialogOpen={isShowFloorDialogOpen}
          bookingId={selectedSchedule.id}
          bookingType={selectedSchedule.bookingType as BookingType}
          highlight={selectedSchedule.bookingInventoryId}
          closeFn={handleShowFloorCloseFn}
          labelButton={t("Close")}
          title={
            selectedSchedule.bookingType === BookingType.PLACEZONE ||
            selectedSchedule.bookingType === BookingType.CONFERENCEZONE
              ? t("_scheduleTable_floorPlanDialog_bookedRoom")
              : t("_scheduleTable_floorPlanDialog_bookedPlace")
          }
        />
      )}
    </>
  );

  if (embed?.contentContainerSx) {
    return <Box sx={scheduleEmbedRootSx}>{scheduleBody}</Box>;
  }

  return scheduleBody;
};

export default Schedule;
